import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  NgZone,
  Output,
  ViewChild,
  input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ExposedPromise } from '@ay/util/exposed-promise';
import { firstValueFrom, lastValueFrom, tap } from 'rxjs';
import { IconComponent } from '../../common/icon';
import { DialogService } from '../../dialog';
import { FacebookService } from '../facebook.service';
import {
  BASE_NAVIGATE_PATH,
  GosuLoginConfigDto,
  IBackstageLoginService,
  LOGIN_SERVICE,
} from '../login.service';

export type SnsType = 'LINE' | 'Facebook' | 'Google';
export type SnsMode = 'REGISTER' | 'LOGIN' | 'BIND' | 'UNBIND';

@Component({
  selector: 'gosu-sns',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, IconComponent, CommonModule],
  styles: [
    `
      :host ::ng-deep .mdc-button__label {
        width: 100%;
        display: block;
      }
    `,
  ],
  templateUrl: './gosu-sns.component.html',
})
export class GosuSnsComponent implements AfterViewInit {
  @ViewChild('googleOneTap')
  public googleOneTap!: ElementRef<HTMLDivElement>;

  @Output()
  public statusChange = new EventEmitter();

  public type = input.required<SnsType>();

  public mode = input.required<SnsMode>();

  public redirectTo = input<string>('');

  public constructor(
    @Inject(LOGIN_SERVICE)
    private readonly _loginService: IBackstageLoginService,
    @Inject(BASE_NAVIGATE_PATH)
    private readonly _baseNavigatePath: string,
    private readonly _applicationRef: ApplicationRef,
    private readonly _facebookService: FacebookService,
    private readonly _matSnackbar: MatSnackBar,
    private readonly _router: Router,
    private readonly _dialogService: DialogService,
    private readonly _ngZone: NgZone,
  ) {}

  public async ngAfterViewInit() {
    const config = await firstValueFrom(this._loginService.config$);

    this._applicationRef.tick();
    switch (this.type()) {
      case 'Facebook':
        this._initFacebook(config);
        break;

      case 'Google':
        this._initGoogle(config);
        break;
    }
  }

  public onClick() {
    switch (this.mode()) {
      case 'REGISTER':
        this._register();
        break;

      case 'LOGIN':
        this._login();
        break;

      case 'BIND':
        this._bind();
        break;

      case 'UNBIND':
        this._unbind();
        break;
    }
  }

  private async _initFacebook(config: GosuLoginConfigDto) {
    if (!config.facebook.enabled) {
      return;
    }

    await this._facebookService.init({
      appId: config.facebook.appId,
      version: config.facebook.apiVersion as string,
    });
  }

  private async _initGoogle(config: GosuLoginConfigDto) {
    if (!config.google.enabled) {
      return;
    }

    if (this.mode() === 'UNBIND') {
      return;
    }

    await this._loadGoogleScript();

    if (this.mode() === 'LOGIN' || this.mode() === 'BIND') {
      window.google.accounts.id.initialize({
        client_id: config.google.clientId as string,
        callback: (response: google.accounts.id.CredentialResponse) => {
          this._ngZone.run(() => {
            this._loginGoogle(response);
          });
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      google.accounts.id.renderButton(this.googleOneTap.nativeElement, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        logo_alignment: 'left',
        width: this.googleOneTap.nativeElement.clientWidth,
      });
    }
  }

  private async _loginGoogle(response: google.accounts.id.CredentialResponse) {
    switch (this.mode()) {
      case 'LOGIN':
        this._loginViaGoogle(response);
        break;

      case 'BIND':
        this._bindGoogle(response);
        break;

      case 'UNBIND':
        this._unbindGoogle();
        break;
    }
  }

  private async _loadGoogleScript() {
    let script: HTMLScriptElement;
    if (!document.getElementById('googleOneTap')) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = 'googleOneTap';
      document.head.appendChild(script);

      const script$ = new ExposedPromise();
      script.onload = () => script$.resolve();
      await script$.promise;
    } else {
      script = document.getElementById('googleOneTap') as HTMLScriptElement;
      if (!google) {
        const script$ = new ExposedPromise();
        script.onload = () => script$.resolve();
        await script$.promise;
      }
    }
  }

  private _login() {
    switch (this.type()) {
      case 'LINE':
        this._loginViaLine();
        break;

      case 'Facebook':
        this._loginViaFacebook();
        break;
    }
  }

  private _register() {
    switch (this.type()) {
      case 'LINE':
        this._registerViaLine();
        break;

      case 'Facebook':
        this._registerViaFacebook();
        break;

      case 'Google':
        this._registerViaGoogle();
        break;
    }
  }

  private _bind() {
    switch (this.type()) {
      case 'LINE':
        this._bindLine();
        break;

      case 'Facebook':
        this._bindFacebook();
        break;
    }
  }

  private _unbind() {
    switch (this.type()) {
      case 'LINE':
        this._unbindLine();
        break;

      case 'Facebook':
        this._unbindFacebook();
        break;

      case 'Google':
        this._unbindGoogle();
        break;
    }
  }

  private async _loginViaFacebook(): Promise<void> {
    let authResponse: { accessToken: string };
    let status: string;

    try {
      ({ authResponse, status } = await this._facebookService.login({
        scope: 'email',
        return_scopes: true,
        enable_profile_selector: true,
      }));

      if (status !== 'connected') {
        this._matSnackbar.open(`取得 Facebook 授權時發生錯誤，請重新嘗試`);
        return;
      }
    } catch (error: any) {
      console.error(error);
      return;
    }

    try {
      const token = await this._loginService.loginViaFb(
        authResponse.accessToken,
      );
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (err: any) {
      if (err.code === 'OAUTH_ACCOUNT_NOT_FOUND') {
        this._router.navigate(
          [this._baseNavigatePath, 'login', 'register-via-facebook'],
          {
            queryParams: { type: 'facebook', redirectTo: this.redirectTo },
          },
        );
        return;
      } else if (err.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(err);
    }
  }

  private async _loginViaGoogle(
    response: google.accounts.id.CredentialResponse,
  ) {
    try {
      sessionStorage.setItem('googleOneTap', JSON.stringify(response));
      const token = await this._loginService.loginViaGoogle(
        response.credential,
      );
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (error: any) {
      if (error.error === 'popup_closed_by_user') {
        return;
      } else if (error.code === 'OAUTH_ACCOUNT_NOT_FOUND') {
        this._router.navigate(
          [this._baseNavigatePath, 'login', 'register-via-google'],
          {
            queryParams: { type: 'google', redirectTo: this.redirectTo },
          },
        );
        return;
      } else if (error.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(error);
    }
  }

  private async _loginViaLine() {
    try {
      await lastValueFrom(this._lineLogin());
      const token = await this._loginService.loginViaLine();
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (error: any) {
      if (error.code === 'OAUTH_ACCOUNT_NOT_FOUND') {
        this._router.navigate(
          [this._baseNavigatePath, 'login', 'register-via-line'],
          {
            queryParams: { redirectTo: this.redirectTo },
          },
        );
        return;
      } else if (error.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(error);
    }
  }

  private async _registerViaLine(): Promise<void> {
    try {
      const token = await this._loginService.registerViaLine();
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (error: any) {
      if (error.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(error);
    }
  }

  private async _registerViaFacebook(): Promise<void> {
    try {
      const user = await this._facebookService.api('/me', 'get', {
        fields: 'email,name',
      });

      const token = await this._loginService.registerViaFb(
        this._facebookService.getAuthResponse().accessToken,
        user.name,
      );
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (error: any) {
      if (error.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(error);
    }
  }

  private async _registerViaGoogle() {
    try {
      const googleOneTap = sessionStorage.getItem('googleOneTap');
      if (!googleOneTap) throw new Error('googleOneTap not found');

      const response: google.accounts.id.CredentialResponse =
        JSON.parse(googleOneTap);
      const token = await this._loginService.registerViaGoogle(
        response.credential,
      );
      this._loginService.loginViaToken(token);
      this.statusChange.emit(true);
    } catch (error: any) {
      if (error.error === 'popup_closed_by_user') {
        return;
      } else if (error.code === 'OAUTH_ACCOUNT_NOT_FOUND') {
        this._router.navigate(
          [this._baseNavigatePath, 'login', 'register-via-google'],
          {
            queryParams: { type: 'google', redirectTo: this.redirectTo },
          },
        );
        return;
      } else if (error.code === 'EMAIL_EXIST_BUT_NOT_BIND') {
        return this._showEmailExistNotBindError();
      }

      console.error(error);
    }
  }

  private async _bindFacebook() {
    const response = await this._facebookService.login({
      enable_profile_selector: true,
      return_scopes: true,
      scope:
        'email,pages_manage_metadata,pages_messaging,pages_read_user_content,pages_read_engagement,pages_manage_posts,pages_manage_engagement,pages_read_engagement,pages_manage_ads',
    });

    if (!response || response.status != 'connected') {
      return this._dialogService.failure(`綁定失敗`);
    }

    try {
      await this._loginService.bindFb(response.authResponse.accessToken);
      this._dialogService.success(`綁定 Facebook 成功`);
    } catch (error: any) {
      await this._processBindError(error);
    }

    this.statusChange.emit(true);
  }

  private async _unbindFacebook() {
    try {
      await this._loginService.unbindFb();
      this._dialogService.success(`解除綁定成功`);
    } catch (error: any) {
      await this._processUnbindError(error);
    }

    this.statusChange.emit(true);
  }

  private async _bindLine() {
    try {
      await this._loginService.bindLine();
      this._dialogService.success(`綁定 LINE 成功`);
    } catch (error: any) {
      await this._processBindError(error);
    }

    this.statusChange.emit(true);
  }

  private async _unbindLine() {
    try {
      await this._loginService.unbindLine();
      this._dialogService.success(`解除綁定成功`);
    } catch (error: any) {
      await this._processUnbindError(error);
    }

    this.statusChange.emit(true);
  }

  private async _bindGoogle(response: google.accounts.id.CredentialResponse) {
    try {
      await this._loginService.bindGoogle(response.credential);
      this._dialogService.success(`綁定 Google 成功`);
    } catch (error: any) {
      if (error.error === 'popup_closed_by_user') {
        return;
      } else {
        await this._processBindError(error);
      }
    }

    this.statusChange.emit(true);
  }

  private async _unbindGoogle() {
    try {
      await this._loginService.unbindGoogle();
      await this._dialogService.success(`解除綁定成功`);
      window.location.reload();
    } catch (error: any) {
      await this._processUnbindError(error);
    }
    this.statusChange.emit(true);
  }

  private async _processBindError(error: { code: string; message: string }) {
    if (error.code === 'SOMEONE_BIND') {
      this._dialogService.failure(error.message);
      return;
    } else if (error.code === 'ALREADY_BIND') {
      return;
    } else if (error.code === 'PERMISSION_DENIED_NEED_LINE_LOGIN') {
      await lastValueFrom(this._lineLogin());
      await this._bindLine();
      return;
    }

    throw error;
  }

  private async _processUnbindError(error: { code: string; message: string }) {
    if (error.code === 'NOT_YET_BIND') {
      return;
    }
    throw error;
  }

  private _lineLogin() {
    return this._loginService.lineLogin().pipe(
      tap(async (event) => {
        if (event.status !== 'INITIAL') {
          return;
        }

        window.open(
          event.url,
          '_new',
          'width=500,height=500,popup=yes,scrollbars=true,resizable=no',
        );
      }),
    );
  }

  private _showEmailExistNotBindError() {
    this._dialogService.failure(`尚未綁定 ${this.type()} 登入`, {
      messages: [
        `該 Email 已經是 GOSU 的會員，`,
        `但尚未綁定 ${this.type()} 登入。`,
        ``,
        `請先透過帳號密碼登入`,
        `並在個人資料管理頁面綁定 ${this.type()}`,
      ],
    });
  }
}
